import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import ChipsDropDown from '../components/ChipsDropDown';
import { ProductsContext } from '../../ProductsContext';
import ChipsDropDownWithComparison from '../components/ChipsDropDownWithComparison';
import { BlockRightArrowIcon } from 'assests/GmsIcons';

type TReviewProps = {
    selectedProducts: TOption[],
    setSelectedProducts: Dispatch<SetStateAction<TOption[]>>
    payload: TRecategorizePayload
    finalPayload: TRecategorizePayload
    setPayload: Dispatch<SetStateAction<TRecategorizePayload>>
    setFinalPayload: Dispatch<SetStateAction<TRecategorizePayload>>
}

const Review = ({ selectedProducts, setSelectedProducts, payload, setPayload, setFinalPayload }: TReviewProps) => {
    const [commonAttributes, setCommonAttributes] = useState<TOption[]>([{ id: "string", name: "string" }])
    const [transferAttributes, setTransferAttributes] = useState<TOption[]>([{ id: "string", name: "string" }])
    const [mapAttributes, setMapAttributes] = useState<{ sourceName: string, targetName: string }[]>([{ sourceName: "string", targetName: "string" }])
    const [removedAttributes, setRemovedAttributes] = useState<TOption[]>([])
    const reviewObj = [
        {
            header: "Move Products",
            options: selectedProducts,
            setOptions: setSelectedProducts
        },
        {
            header: "Common Attributes",
            options: commonAttributes,
            setOptions: setCommonAttributes
        },
        {
            header: "Transfer Attributes",
            options: transferAttributes,
            setOptions: setTransferAttributes
        },
        {
            header: "Values To Be Removed",
            options: removedAttributes,
            setOptions: setRemovedAttributes
        }
    ]

    /**
     * Builds every summary list AND `finalPayload` in ONE pass.
     *
     * This used to be two useEffects that each called `setFinalPayload({ ...payload, <one field> })`.
     * Neither built on the other's result, so whichever ran last silently reverted the other's
     * write -- and the common-attributes one computed an empty `final_common_attr_list` that was
     * never populated, so it only ever appeared to work because the unique-attributes effect ran
     * second and spread `payload.common_attr_list` back in. One effect, both fields, no clobber.
     *
     * BOTH lists now go to the backend VERBATIM, unchecked rows included. That is deliberate: the
     * backend derives keep-vs-drop from each row's own `checked` flag
     * (`Entities/product.py:re_categorise` builds `unchecked_attributes` from the rows it receives),
     * so filtering unchecked rows out here does not mean "leave them alone" -- it means the backend
     * never hears about them and cannot act. Dropping unchecked UNIQUE rows is exactly what made
     * unchecking one a silent no-op: the product kept ATTR_VAL_FOR_PRODUCT edges to values of an
     * attribute the target category has no ATTRIBUTE_FOR_CATEGORY edge to.
     */
    const buildFinalPayloadAndSummaries = () => {
        const commonAttrList = payload?.common_attr_list ?? []
        const uniqueAttrList = payload?.unique_attr_source_list ?? []

        const commonAttributesArray: TOption[] = []
        const transferAttributesArray: TOption[] = []
        const mapAttributesArray: { sourceName: string, targetName: string }[] = []
        // Every attribute whose values this move will DELETE from the selected products -- from
        // both lists, because unchecking means the same thing on each. Surfaced so the destructive
        // half of the move is confirmed on this screen rather than discovered afterwards.
        const removedAttributesArray: TOption[] = []

        commonAttrList.forEach(attrObj => {
            const chip = { id: attrObj?.attribute_id as string, name: attrObj?.attribute_name as string }
            if (attrObj?.checked) commonAttributesArray.push(chip)
            else removedAttributesArray.push(chip)
        })

        uniqueAttrList.forEach(attrObj => {
            const chip = { id: attrObj?.attribute_id as string, name: attrObj?.attribute_name as string }
            if (!attrObj?.checked) {
                removedAttributesArray.push(chip)
                return
            }
            if (attrObj?.action === 'transfer')
                transferAttributesArray.push(chip)
            else if (attrObj?.action === 'map')
                mapAttributesArray.push({ sourceName: attrObj?.attribute_name as string, targetName: attrObj?.target_attribute_name as string })
        })

        setCommonAttributes(commonAttributesArray)
        setTransferAttributes(transferAttributesArray)
        setMapAttributes(mapAttributesArray)
        setRemovedAttributes(removedAttributesArray)
        setFinalPayload({
            ...payload,
            common_attr_list: commonAttrList,
            unique_attr_source_list: uniqueAttrList,
        })
    }

    useEffect(() => {
        buildFinalPayloadAndSummaries()
    }, [payload?.common_attr_list, payload?.unique_attr_source_list])


    return (
        <div className='tw-justify-center tw-items-center'>
            <div className="tw-w-[350px] tw-text-ink tw-text-sm tw-font-semibold tw-mt-4">Review the changes</div>
            <div className='tw-flex tw-justify-between tw-mt-4'>
                <div className='tw-w-[45%]'>
                    <div className="tw-text-ink-tertiary tw-text-sm tw-font-medium tw-mb-2">(From) Source </div>
                    <div className=" tw-w-full tw-px-2.5 tw-py-2 tw-bg-surface tw-rounded tw-border tw-border-edge tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                        <div className="tw-grow tw-shrink tw-basis-0 tw-text-ink tw-text-sm tw-font-normal">{payload?.source_category_name}</div>
                    </div>
                </div>
                <div className='tw-mt-10'>
                    <BlockRightArrowIcon />
                </div>
                <div className='tw-w-[45%]'>
                    <div className="tw-text-ink-tertiary tw-text-sm tw-font-medium tw-mb-2">(To) Target </div>
                    <div className="tw-w-full tw-px-2.5 tw-py-2 tw-bg-surface tw-rounded tw-border tw-border-edge tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                        <div className="tw-grow tw-shrink tw-basis-0 tw-text-ink tw-text-sm tw-font-normal">{payload?.target_category_name}</div>
                    </div>
                </div>
            </div>
            {reviewObj?.map(x =>
                // keyed on the header (stable, unique per section) — this map had no key, which
                // React warns about on every render of this screen
                <div key={x?.header} className='tw-w-[700px] tw-mt-4 tw-mb-4'>
                    {
                        <ChipsDropDown
                            header={x?.header}
                            options={x?.options}
                            setOptions={x?.setOptions}
                            remove={false}
                        />
                    }
                </div>)}
            <div className='tw-w-[700px] tw-mt-4 tw-mb-4'>
                {
                    <ChipsDropDownWithComparison
                        header={"Map Attributes"}
                        options={mapAttributes}
                    />
                }
            </div>

        </div>

    );
}

export default Review;